// Helper fetch condiviso fra i client API.
// Inviamo sempre `credentials: 'include'` per i cookie httpOnly.
//
// Refresh trasparente del JWT (sliding session): se il backend risponde 401 a una
// chiamata che NON sia di auth, tentiamo una volta `POST /api/auth/refresh` e poi
// riproviamo la richiesta originale. Se il refresh fallisce (refresh token scaduto
// o assente), emettiamo un evento `audiq:auth-expired` su `window` — un listener
// globale registrato in `main.tsx` esegue il redirect hard a `/admin/login`.
//
// Le chiamate concorrenti durante il refresh in volo condividono la stessa Promise
// (singleton `refreshInFlight`) per evitare di martellare l'endpoint refresh quando
// più query partono insieme.

import { ApiErrorSchema } from '@audiq/shared';

export class ApiClientError extends Error {
  status: number;
  payload: ReturnType<typeof ApiErrorSchema.parse>;

  constructor(status: number, payload: ReturnType<typeof ApiErrorSchema.parse>) {
    super(payload.error);
    this.status = status;
    this.payload = payload;
  }
}

// Path di auth per cui NON dobbiamo tentare refresh: o sono il refresh stesso (loop),
// o sono il login flow (non c'è ancora una sessione da rinfrescare).
const AUTH_PATHS_NO_REFRESH = new Set<string>([
  '/api/auth/login',
  '/api/auth/login/verify-2fa',
  '/api/auth/refresh',
  '/api/auth/logout',
]);

let refreshInFlight: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      // Reset al prossimo tick: se altre 401 arrivano DOPO che il refresh è
      // concluso, vogliamo che ripartano un nuovo refresh (la sessione potrebbe
      // essere scaduta di nuovo dopo molto tempo).
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

function emitAuthExpired(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('audiq:auth-expired'));
}

async function rawFetch<T>(
  path: string,
  init: RequestInit,
): Promise<{ ok: true; data: T } | { ok: false; status: number; payload: unknown }> {
  // Fastify rifiuta richieste con Content-Type: application/json ma body vuoto
  // (FST_ERR_CTP_EMPTY_JSON_BODY) → l'header va inviato solo se c'è davvero un body.
  const hasBody = init.body !== undefined && init.body !== null;
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  const json: unknown = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, status: res.status, payload: json };
  return { ok: true, data: json as T };
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const first = await rawFetch<T>(path, init);
  if (first.ok) return first.data;

  // Tentativo di refresh + retry: solo per 401 fuori dalle rotte di auth.
  if (first.status === 401 && !AUTH_PATHS_NO_REFRESH.has(path)) {
    const refreshed = await performRefresh();
    if (refreshed) {
      const second = await rawFetch<T>(path, init);
      if (second.ok) return second.data;
      // Se anche dopo refresh il backend rifiuta → emit auth-expired e propaga errore.
      if (second.status === 401) emitAuthExpired();
      const errorParsed = ApiErrorSchema.safeParse(second.payload);
      throw new ApiClientError(
        second.status,
        errorParsed.success
          ? errorParsed.data
          : { ok: false, error: 'Errore sconosciuto', code: 'UNKNOWN' },
      );
    }
    // Refresh fallito → la sessione è davvero finita.
    emitAuthExpired();
  }

  const errorParsed = ApiErrorSchema.safeParse(first.payload);
  throw new ApiClientError(
    first.status,
    errorParsed.success
      ? errorParsed.data
      : { ok: false, error: 'Errore sconosciuto', code: 'UNKNOWN' },
  );
}
